"use client";

import { useState, type ReactNode } from "react";
import * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";
import type { StaticNavLink } from "@/config/nav-links";
import NavMegaMenu from "./NavMegaMenu";
import MarketsMegaMenu from "./MarketsMegaMenu";
import { useNavTheme, useNavThemeStyles } from "./nav-theme-context";
import TrackedLink from "./TrackedLink";

const navigationMenuTriggerStyle = cva(
    "group inline-flex h-auto w-max items-center justify-center bg-transparent text-base font-regular leading-[22px] transition-colors hover:opacity-80 focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=open]:font-bold",
);

type StaticNavLinksProps = {
    links: StaticNavLink[];
    logo: ReactNode;
    children?: ReactNode;
};

const isStandaloneLink = (link: StaticNavLink): link is StaticNavLink & { href: string } =>
    Boolean(link.href) && !link.sections?.length && !link.categories?.length;

const StaticNavLinks = ({ links, logo, children }: StaticNavLinksProps) => {
    const theme = useNavTheme();
    const styles = useNavThemeStyles();
    const [openNavItem, setOpenNavItem] = useState("");

    return (
        <NavigationMenuPrimitive.Root
            className="relative hidden w-full px-5 sm:flex sm:items-center sm:justify-between"
            onValueChange={setOpenNavItem}
        >
            <div className="flex flex-1 items-center gap-[60px]">
                {logo}
                <NavigationMenuPrimitive.List className="group flex list-none items-center gap-9">
                    {links.map((link) => (
                        <NavigationMenuPrimitive.Item key={link.label} value={link.label}>
                            {isStandaloneLink(link) ? (
                                <NavigationMenuPrimitive.Link asChild>
                                    <TrackedLink
                                        href={link.href}
                                        event={link.eventName}
                                        cleverTapEvent={link.branchEvent}
                                        target={link.openInNewTab ? "_blank" : undefined}
                                        rel={
                                            link.openInNewTab
                                                ? "noopener noreferrer"
                                                : undefined
                                        }
                                        className={cn(
                                            navigationMenuTriggerStyle(),
                                            "no-underline",
                                            styles.link,
                                        )}
                                    >
                                        {link.label}
                                    </TrackedLink>
                                </NavigationMenuPrimitive.Link>
                            ) : (
                                <>
                                    <NavigationMenuPrimitive.Trigger
                                        className={cn(
                                            navigationMenuTriggerStyle(),
                                            "group",
                                            styles.link,
                                            theme === "blue"
                                                ? "data-[state=open]:text-[#FBCBAD]"
                                                : "data-[state=open]:text-brandAccent",
                                        )}
                                    >
                                        {link.label}
                                    </NavigationMenuPrimitive.Trigger>
                                    <NavigationMenuPrimitive.Content className="w-full data-[motion^=from-]:animate-in data-[motion^=to-]:animate-out data-[motion^=from-]:fade-in data-[motion^=to-]:fade-out data-[motion=from-end]:slide-in-from-right-52 data-[motion=from-start]:slide-in-from-left-52 data-[motion=to-end]:slide-out-to-right-52 data-[motion=to-start]:slide-out-to-left-52">
                                        {link.layout === "sidebar" && link.categories ? (
                                            <MarketsMegaMenu
                                                sidebarTitle={link.sidebarTitle}
                                                categories={link.categories}
                                                isOpen={openNavItem === link.label}
                                            />
                                        ) : (
                                            <NavMegaMenu sections={link.sections ?? []} />
                                        )}
                                    </NavigationMenuPrimitive.Content>
                                </>
                            )}
                        </NavigationMenuPrimitive.Item>
                    ))}
                </NavigationMenuPrimitive.List>
            </div>

            {children}

            <div className="absolute inset-x-0 top-full z-20 w-full">
                <NavigationMenuPrimitive.Viewport
                    className={cn(
                        "h-[var(--radix-navigation-menu-viewport-height)] !w-full overflow-hidden rounded-b-2xl shadow-[0_4px_15px_0_rgba(0,0,0,0.07)] data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-90",
                        // Learn is a single row — let viewport height follow content; keep min-h for Products/Markets.
                        openNavItem === "Learn" ? "min-h-0" : "min-h-[300px]",
                        styles.viewport,
                    )}
                    style={{ width: "100%" }}
                />
            </div>
        </NavigationMenuPrimitive.Root>
    );
};

export default StaticNavLinks;
